import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";
import { toast } from "sonner";

import {
  FileText, Search, Filter, Calendar, 
  User, ShieldCheck, Clock, Hash, 
  RefreshCw, Terminal, AlertCircle, HardDrive,
  Info, ShieldAlert, LifeBuoy, HandHelping
} from "lucide-react";

export default function AdminLogs() {
  const navigate = useNavigate();
  const { authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadLogs = async () => {
    try {
      setLoading(true);
      // Busca os últimos 100 logs para não pesar o sistema
      const q = query(collection(db, "logs"), orderBy("timestamp", "desc"), limit(100));
      const snap = await getDocs(q);
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar trilha de auditoria.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  // --- FILTRAGEM ---
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const term = search.toLowerCase();
      const matchesSearch = 
        (log.adminName || "").toLowerCase().includes(term) ||
        (log.action || "").toLowerCase().includes(term) ||
        (log.details || "").toLowerCase().includes(term);

      const matchesRole = roleFilter === "all" ? true : log.adminRole === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [logs, search, roleFilter]);

  const getRoleBadge = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': 
        return <Badge className="bg-red-100 text-red-700 border-none font-black text-[8px] uppercase px-2 italic flex items-center gap-1"><ShieldAlert size={10}/> Admin</Badge>;
      case 'support': 
        return <Badge className="bg-blue-100 text-blue-700 border-none font-black text-[8px] uppercase px-2 italic flex items-center gap-1"><LifeBuoy size={10}/> Suporte</Badge>;
      case 'helper': 
        return <Badge className="bg-green-100 text-green-700 border-none font-black text-[8px] uppercase px-2 italic flex items-center gap-1"><HandHelping size={10}/> Helper</Badge>;
      default: 
        return <Badge className="bg-slate-100 text-slate-500 border-none font-black text-[8px] uppercase px-2 italic">Sistema</Badge>;
    }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400 uppercase">Acessando Arquivos...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Logs do <span className="text-blue-600">Sistema</span></h1>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                 <button onClick={() => setRoleFilter("all")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${roleFilter === 'all' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-100'}`}>Todos</button>
                 <button onClick={() => setRoleFilter("admin")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${roleFilter === 'admin' ? 'bg-red-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-100'}`}>Admins</button>
                 <button onClick={() => setRoleFilter("support")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${roleFilter === 'support' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-100'}`}>Suporte</button>
                 <button onClick={() => setRoleFilter("helper")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${roleFilter === 'helper' ? 'bg-green-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-100'}`}>Helpers</button>
              </div>
            </div>
            <div className="relative w-full md:w-96 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
              <input 
                type="text" 
                placeholder="BUSCAR AÇÃO OU RESPONSÁVEL..." 
                className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none focus:ring-2 focus:ring-blue-500/20 transition-all" 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
          </header>

          <div className="space-y-4">
            <Card className="rounded-3xl border-none shadow-sm bg-slate-900 text-white p-4">
               <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <Terminal size={14} className="text-blue-400" /> Console de Atividades Recentes
               </div>
            </Card>

            {loading ? (
                <div className="py-20 text-center font-black text-slate-300 animate-pulse uppercase tracking-widest">Lendo registros de segurança...</div>
            ) : filteredLogs.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200 uppercase font-black text-slate-300 italic">Nenhum log encontrado para este filtro.</div>
            ) : filteredLogs.map((log) => (
                <Card key={log.id} className="rounded-[1.5rem] border-none shadow-md bg-white hover:shadow-lg transition-all">
                  <CardContent className="p-5">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                      
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                          <Clock size={20} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-[11px] font-black text-slate-900 uppercase italic tracking-tighter bg-slate-100 px-2 py-0.5 rounded-md">
                               {log.timestamp ? new Date(log.timestamp).toLocaleString('pt-BR') : 'Sem data'}
                            </span>
                            {getRoleBadge(log.adminRole)}
                            <span className="text-[11px] font-bold text-blue-600 uppercase">@{log.adminName || 'Sistema'}</span>
                          </div>
                          <p className="text-sm font-bold text-slate-700 uppercase italic leading-tight">{log.action}</p>
                        </div>
                      </div>

                      <div className="w-full lg:w-auto bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <p className="text-[11px] font-semibold text-slate-500 italic leading-relaxed">
                           <span className="font-black text-slate-400 mr-1 uppercase">Detalhes:</span> 
                           {log.details || "Sem informações adicionais."}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                            <span className="text-[8px] font-mono text-slate-300 uppercase tracking-widest">REF: {log.id.slice(-10)}</span>
                            <Info size={12} className="text-slate-200" />
                        </div>
                      </div>

                    </div>
                  </CardContent>
                </Card>
            ))}
          </div>

          <footer className="mt-12 p-8 bg-white rounded-[2.5rem] border border-slate-100 text-center">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                  <HardDrive size={24} />
              </div>
              <h4 className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Segurança de Dados</h4>
              <p className="text-[10px] font-bold text-slate-400 uppercase italic">Os logs são imutáveis e armazenados para fins de auditoria conforme a LGPD.</p>
          </footer>
        </main>
      </div>
    </div>
  );
}